import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import type { ActiveSubscription, FormattedSubscription } from '@/types'
import { useSubscriptionStore } from '@/state'
import { subscriptionService } from '@/api'

export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  active: () => [...subscriptionKeys.all, 'active'] as const,
  history: () => [...subscriptionKeys.all, 'history'] as const,
  detail: (subscriptionId: number) =>
    [...subscriptionKeys.all, 'detail', subscriptionId] as const,
}

/**
 * Formats a date according to the specified format.
 * @param date The date to format.
 * @param format The formatting to apply (localization).
 * @returns A formatted date string.
 */
const formatDate = (date: Date, format?: 'HU' | 'EU' | 'US'): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }

  switch (format) {
    default:
    case 'HU':
      return date.toLocaleDateString('hu-HU', options)

    case 'EU':
      return date.toLocaleDateString('en-GB', options)

    case 'US':
      return date.toLocaleDateString('en-US', options)
  }
}

/**
 * Formats a nullable date according to the specified format.
 * @param date The date to format. Can be null.
 * @param format The format of the expected date. Can be null, defaults to Hungarian.
 * @returns A formatted date or undefined, if the input date was undefined.
 */
const formatNullableDate = (
  date?: Date,
  format?: 'HU' | 'EU' | 'US'
): string | undefined => {
  if (!date) return undefined
  return formatDate(date, format)
}

/**
 * Transforms raw subscription data into a formatted subscription for UI use.
 * @param data The subscription data to transform.
 * @param dateFormat The date format to apply when formatting date values.
 * @returns A formatted subscription object to be used on the UI.
 */
const transformSubscription = (
  data: ActiveSubscription,
  dateFormat: 'HU' | 'EU' | 'US'
): FormattedSubscription => {
  // Helper to parse date without time-zone shifting
  const parseDateOnly = (dateString: string) => {
    // Takes "2026-12-31T23:59:59Z" and keeps only "2026-12-31"
    const datePart = dateString.split('T')[0]
    return new Date(datePart)
  }

  const parseNullableDateOnly = (dateString?: string) => {
    if (!dateString) return undefined
    return parseDateOnly(dateString)
  }

  const nextBillingDate = data.nextBillingDate
    ? parseDateOnly(data.nextBillingDate)
    : undefined

  const terminationDate = parseNullableDateOnly(data.terminatesAtUtc)
  const startedAt = new Date(data.startDate)

  // PastDue must stay cancelable — the backend cancels the subscription and
  // deletes the card registration even while charges are failing.
  const canCancel =
    (data.status === 'Active' || data.status === 'PastDue') &&
    data.billingCycle === 'Recurring'

  return {
    ...data,
    nextBillingDate,
    terminationDate,
    startedAt,
    formattedNextBilling: nextBillingDate
      ? formatDate(nextBillingDate, dateFormat)
      : undefined,
    formattedTermination: formatNullableDate(terminationDate, dateFormat),
    formattedStartedAt: formatDate(startedAt, dateFormat),
    canCancel,
  }
}

/**
 * Hook to get the active subscription of the user.
 * @param options React Query options to govern data retrieval.
 * @returns A React Query object.
 */
export function useSubscription(
  options?: Omit<
    UseQueryOptions<
      ActiveSubscription | null,
      Error,
      FormattedSubscription | null
    >,
    'queryKey' | 'queryFn' | 'select'
  >
) {
  const dateFormat = useSubscriptionStore((state) => state.dateFormat)

  return useQuery({
    queryKey: subscriptionKeys.active(),
    queryFn: async () => {
      try {
        return await subscriptionService.fetchActiveSubscription()
      } catch (error) {
        // 404 means the user has no active subscription — a natural
        // state, not an error
        if (isAxiosError(error) && error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    select: (data: ActiveSubscription | null) =>
      data ? transformSubscription(data, dateFormat) : null,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    retry: (failureCount, error) => {
      // Don't retry on 404 (no subscription)
      if (error.message.includes('404')) return false
      // Retry up to 3 times for other errors
      return failureCount < 3
    },
    ...options,
  })
}

export function useCancelSubscription() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => subscriptionService.cancelActiveSubscription(),
    onSuccess: () => {
      // Invalidate and refetch the active subscription data,
      // so the UI automatically refreshes to show the cancelled status
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.all,
      })
    },
  })
}
